import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Conversations from '../Conversations';
import Icon from './icons/Icon';
import './ConversationsTopBar.css';

const ConversationsTopBar = ({onNewChat, currentConversationId}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    loadRecentConversations();
    
    const handleConversationSaved = async (event) => {
      const savedConversationId = event.detail?.conversationId;
      
      setConversations(prevConvs => {
        const existingIndex = prevConvs.findIndex(conv => conv._id === savedConversationId);
        
        if (existingIndex >= 0) {
          const updated = [...prevConvs];
          updated[existingIndex] = {
            ...updated[existingIndex],
            updatedAt: new Date().toISOString()
          };
          return updated.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA;
          });
        } else {
          Conversations.fetchConversation(savedConversationId).then(result => {
            if (result.success) {
              const newConv = {
                _id: savedConversationId,
                conversationName: result.conversationName,
                messages: result.messages,
                sessionId: result.sessionId,
                updatedAt: new Date().toISOString(),
                createdAt: new Date().toISOString()
              };
              setConversations(prev => {
                const filtered = prev.filter(conv => conv._id !== savedConversationId);
                return [newConv, ...filtered].slice(0, 10);
              });
            }
          }).catch(error => {
            console.error('Failed to fetch new conversation:', error);
          });
          return prevConvs;
        }
      });
    };
    
    window.addEventListener('conversationSaved', handleConversationSaved);
    
    return () => {
      window.removeEventListener('conversationSaved', handleConversationSaved);
    };
  }, [currentConversationId]);

  const loadRecentConversations = async () => {
    try {
      setLoading(true);
      const result = await Conversations.searchConversations('');
      if (result.success) {
        const sorted = result.results.sort((convA, convB) => {
          const dateA = new Date(convA.updatedAt || convA.createdAt || 0);
          const dateB = new Date(convB.updatedAt || convB.createdAt || 0);
          return dateB - dateA;
        });
        setConversations(sorted.slice(0, 10));
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationClick = (conversation) => {
    const url = new URL(window.location.href);
    url.searchParams.set('conversationId', conversation._id);
    window.history.pushState({}, '', url);
    window.location.reload();
  };

  const truncateName = (name) => {
    if (!name) return 'Untitled';
    return name.length > 30 ? name.substring(0, 30) + '...' : name;
  };

  return (
    <div className="conversations-top-bar">
      {/* New Chat Button */}
      <button className="top-new-chat" onClick={onNewChat} title="New chat">
        <Icon name="plus" size={16} />
        <span>New chat</span>
      </button>
      
      {/* Conversation Tabs */}
      <div className="conversation-tabs">
        {conversations.map((conv) => (
          <button
            key={conv._id}
            className={`conversation-tab ${currentConversationId === conv._id ? 'active' : ''}`}
            onClick={() => handleConversationClick(conv)}
            title={conv.conversationName || 'Untitled'}
          >
            <Icon name="message" size={14} />
            <span className="tab-name">{truncateName(conv.conversationName)}</span>
            {currentConversationId === conv._id && (
              <button
                className="tab-close"
                onClick={(e) => {
                  e.stopPropagation();
                  onNewChat();
                }}
                title="Close"
              >
                <Icon name="close" size={12} />
              </button>
            )}
          </button>
        ))}
        
        {conversations.length === 0 && !loading && (
          <div className="no-conversations">
            No recent conversations
          </div>
        )}
      </div>
    </div>
  );
};

ConversationsTopBar.propTypes = {
  onNewChat: PropTypes.func.isRequired,
  currentConversationId: PropTypes.string,
};

export default ConversationsTopBar;
